import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { generatePath, useRouteMatch } from 'react-router';

import { useLoggedInUserRoles } from '~/components/Auth';
import { useSelectedEmployeeContext } from '~/components/SelectedEmployee';
import { fetchEmployee, fetchEmployeeEmployment } from '~/data/employee';
import {
  fetchEmployeeDetails,
  useEmployeeProfile,
} from '~/data/employeeProfile';
import { LegacyEmployeeCardPropsType } from '~/legacy-components/LegacyEmployeeCard/component';
import { EmployeeModulePath } from '~/scenes/PortalViews/Views/EmployeeViews/router';
import { EmployeeProfileDto } from '~/services/Http/client.people.generated';
import {
  isRoleHRAdmin,
  isRoleITAdmin,
  isRoleStaffHR,
} from '~/services/Permissions';
import { AppDispatch } from '~/store';

type UseEmployeeViewsOutput = {
  isLoading: boolean;
  failedToLoadData: boolean;
  employeeCard: Omit<LegacyEmployeeCardPropsType, 'profile'> &
    Partial<Pick<LegacyEmployeeCardPropsType, 'profile'>>;
  employeeActionsVisible: boolean;
  availableModules: EmployeeModulePath[];
  hasFullAccess: boolean;
  employeeId: number;
};

export const useEmployeeViews = (): UseEmployeeViewsOutput => {
  const dispatch: AppDispatch = useDispatch();
  const {
    selectedEmployeeId,
    setSelectedEmployeeId,
    selectedEmployeeDetails,
    isSelectedEmployeeLoggedInUser,
  } = useSelectedEmployeeContext();

  // Get Overlapping Employment Href value
  const { path, params } = useRouteMatch<{ employeeId: string }>();
  const getOverlappingEmploymentHref = (employeeId: number): string => {
    return `/v2${generatePath(path, { ...params, employeeId })}`;
  };

  // Profile data syncing
  const {
    profile,
    contracts,
    failedToLoad,
    isLoading: isLoadingEmployeeProfile,
    hasData: hasEmployeeProfileData,
  } = useEmployeeProfile();

  const getProfileId = useCallback(
    (profile: EmployeeProfileDto | undefined): number | undefined =>
      !!profile ? profile.id : undefined,
    [],
  );

  const { isInSync, profileEmployeeId } = useMemo(() => {
    const profileEmployeeId = getProfileId(profile);

    return {
      profileEmployeeId,
      isInSync: profileEmployeeId === selectedEmployeeId,
    };
  }, [selectedEmployeeId, profile, getProfileId]);

  const fetchProfile = useCallback(() => {
    dispatch(fetchEmployeeDetails(selectedEmployeeId.toString()));
    dispatch(fetchEmployee({ empId: selectedEmployeeId.toString() }));
  }, [dispatch, selectedEmployeeId]);

  useEffect(() => {
    if (!!selectedEmployeeId && selectedEmployeeId !== profileEmployeeId) {
      fetchProfile();
    }
  }, [selectedEmployeeId, profileEmployeeId, fetchProfile]);

  useEffect(() => {
    if (
      !!profileEmployeeId &&
      !isLoadingEmployeeProfile &&
      !failedToLoad &&
      selectedEmployeeId !== profileEmployeeId
    ) {
      setSelectedEmployeeId(profileEmployeeId);
    }
  }, [
    selectedEmployeeId,
    setSelectedEmployeeId,
    isLoadingEmployeeProfile,
    failedToLoad,
    profileEmployeeId,
  ]);

  const roles = useLoggedInUserRoles();

  const {
    availableModules,
    employeeActionsVisible,
    hasFullAccess,
    isLoadingSelectedEmployeeDetails,
  } = useMemo(() => {
    const isNotOwnData = !isSelectedEmployeeLoggedInUser;

    const { isInMyUnits, isLoading, isDataInSync } = selectedEmployeeDetails;
    const isLoadingSelectedEmployeeDetails = isLoading || !isDataInSync;

    const hasFullAccess =
      (isInMyUnits && !isLoadingSelectedEmployeeDetails) ||
      isSelectedEmployeeLoggedInUser;

    const hasITAdminAccess = isRoleITAdmin(roles) && isNotOwnData;
    const hasHRAdminAccess = isRoleHRAdmin(roles) && hasFullAccess;
    const hasHRStaffAccess =
      isRoleStaffHR(roles) && hasFullAccess && isNotOwnData;

    const employeeActionsVisible =
      hasITAdminAccess || hasHRAdminAccess || hasHRStaffAccess;

    const availableModules = hasFullAccess
      ? Object.values(EmployeeModulePath)
      : [EmployeeModulePath.People];

    return {
      availableModules,
      employeeActionsVisible,
      hasFullAccess,
      isLoadingSelectedEmployeeDetails,
    };
  }, [isSelectedEmployeeLoggedInUser, selectedEmployeeDetails, roles]);

  useEffect(() => {
    if (
      !!selectedEmployeeId &&
      selectedEmployeeId === profileEmployeeId &&
      hasFullAccess
    ) {
      dispatch(fetchEmployeeEmployment({ empId: selectedEmployeeId }));
    }
  }, [hasFullAccess, selectedEmployeeId, profileEmployeeId, dispatch]);

  return {
    employeeId: selectedEmployeeId,
    isLoading:
      isLoadingEmployeeProfile || isLoadingSelectedEmployeeDetails || !isInSync,
    failedToLoadData: !hasEmployeeProfileData || !profile,
    employeeCard: {
      profile,
      contracts,
      overlappingEmploymentHref: getOverlappingEmploymentHref,
    },
    employeeActionsVisible,
    availableModules,
    hasFullAccess,
  };
};
